import { useMemo, useState } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import SeoHead from '@/components/feature/SeoHead';
import PageHero from '@/components/feature/PageHero';
import ReplayCard from '@/components/feature/ReplayCard';
import { replays } from '@/mocks/replays';
import { formatCount } from '@/utils/time';

const categoryOptions = ['全部', ...Array.from(new Set(replays.map((item) => item.category)))];

const sortOptions = [
  { key: 'latest', label: '按日期最新' },
  { key: 'popular', label: '按播放量' },
  { key: 'longest', label: '按时长' },
];

export default function ReplayPage() {
  const [activeCategory, setActiveCategory] = useState('全部');
  const [sortKey, setSortKey] = useState('latest');
  const [openId, setOpenId] = useState('');

  const list = useMemo(() => {
    const filtered =
      activeCategory === '全部' ? [...replays] : replays.filter((item) => item.category === activeCategory);
    if (sortKey === 'popular') return filtered.sort((a, b) => b.plays - a.plays);
    if (sortKey === 'longest') {
      return filtered.sort(
        (a, b) => Number.parseInt(b.duration, 10) - Number.parseInt(a.duration, 10),
      );
    }
    return filtered.sort((a, b) => b.dateLabel.localeCompare(a.dateLabel));
  }, [activeCategory, sortKey]);

  const totalPlays = replays.reduce((sum, item) => sum + item.plays, 0);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://7kchain.cn/' },
          { '@type': 'ListItem', position: 2, name: '直播回放列表', item: 'https://7kchain.cn/replay' },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: '直播回放列表',
        url: 'https://7kchain.cn/replay',
        inLanguage: 'zh-CN',
        description: '按栏目与日期整理的直播回放列表，标注时长、播放量与内容摘要。',
      },
      {
        '@type': 'ItemList',
        name: '直播回放列表',
        numberOfItems: replays.length,
        itemListElement: list.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.title,
          url: 'https://7kchain.cn/replay',
        })),
      },
    ],
  };

  return (
    <SiteLayout>
      <SeoHead
        title="直播回放列表 - 爆料直播网往期场次补看"
        description="爆料直播网直播回放列表，按栏目、日期和播放量整理往期场次，标注时长与内容摘要，错过的重点场次可以随时补课。"
        keywords="直播回放列表,爆料直播,回放补看,往期直播,回放时长"
        path="/replay"
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow={`已收录 ${replays.length} 条回放 · 累计播放 ${formatCount(totalPlays)} 次`}
        title="直播回放列表"
        desc="错过的场次在这里补回来。可以按栏目筛选，也可以按日期、播放量或时长排序，点击卡片能展开该场回放的详细信息。"
        crumbs={[{ label: '首页', path: '/' }, { label: '直播回放' }]}
      />

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="container-page">
          <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
            <div className="flex flex-wrap gap-2">
              {categoryOptions.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setActiveCategory(item)}
                  className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                    activeCategory === item
                      ? 'border-secondary-500 bg-secondary-500 font-medium text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-600 hover:border-secondary-400 hover:text-secondary-700'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-background-200 pt-3">
              <span className="text-xs text-foreground-500">排序方式</span>
              {sortOptions.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setSortKey(item.key)}
                  className={`whitespace-nowrap rounded-md border px-3 py-1.5 text-xs transition-colors ${
                    sortKey === item.key
                      ? 'border-primary-500 bg-primary-500 font-medium text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <p className="mt-5 text-sm text-foreground-600">
            当前筛选下共 <strong className="font-semibold text-foreground-950">{list.length}</strong> 条回放
          </p>

          {list.length > 0 ? (
            <div className="mt-4 grid grid-cols-1 items-start gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((item) => (
                <ReplayCard
                  key={item.id}
                  item={item}
                  active={openId === item.id}
                  onOpen={(id) => setOpenId((prev) => (prev === id ? '' : id))}
                />
              ))}
            </div>
          ) : (
            <p className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-100 px-5 py-14 text-center text-sm text-foreground-500">
              该栏目暂无可展示的回放，稍后再来看看。
            </p>
          )}
        </div>
      </section>

      <section className="w-full bg-background-100 py-12 md:py-16">
        <div className="container-page grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-700">
              <i className="ri-timer-line" aria-hidden="true" />
            </span>
            <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">回放保留时长</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              常规场次回放保留 30 天，标注为专题的场次会延长至 90 天，过期后会从列表下架。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
              <i className="ri-sort-desc" aria-hidden="true" />
            </span>
            <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">挑选建议</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              时间紧张时优先看播放量靠前、时长在 60 分钟以内的场次，重点信息通常集中在开头与结尾。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-700">
              <i className="ri-information-line" aria-hidden="true" />
            </span>
            <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">关于回放来源</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              回放由主播本人决定是否公开，涉及版权或嘉宾意愿的场次可能不保留，列表会做出相应标注。
            </p>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}